import { HostConsentState, parseOneTrustActiveGroups } from '../consent'

export interface OneTrustHostWindow {
  OptanonActiveGroups?: unknown
  OneTrust?: { OnConsentChanged?: (callback: () => void) => void }
  addEventListener: Window['addEventListener']
  setInterval: Window['setInterval']
  clearInterval: Window['clearInterval']
  setTimeout: Window['setTimeout']
}

export interface OneTrustConsentRelayOptions {
  /** How long to wait for OneTrust to load before answering `unavailable`. */
  loadTimeoutMs?: number
  pollIntervalMs?: number
}

/**
 * Reads OneTrust on the host page for an iframe that asked for it. Nothing runs until `enable()`,
 * so embeds that never ask see no change.
 */
export function createOneTrustConsentRelay(
  win: OneTrustHostWindow,
  onChange: (state: HostConsentState) => void,
  options: OneTrustConsentRelayOptions = {}
) {
  const { loadTimeoutMs = 10_000, pollIntervalMs = 500 }: typeof options = options

  let firstAnswer: Promise<HostConsentState> | null = null
  let lastSent: string | null = null
  let subscribedToOneTrust = false

  const read = (): HostConsentState | null => {
    const activeGroups = parseOneTrustActiveGroups(win.OptanonActiveGroups)
    return activeGroups ? { status: 'ready', activeGroups } : null
  }

  const publish = (state: HostConsentState) => {
    const key = JSON.stringify(state)
    if (key === lastSent) return
    lastSent = key
    onChange(state)
  }

  const subscribeToOneTrust = () => {
    if (subscribedToOneTrust || typeof win.OneTrust?.OnConsentChanged !== 'function') return
    subscribedToOneTrust = true
    win.OneTrust.OnConsentChanged(check)
  }

  // WHY: OneTrust has no unsubscribe and may load after us; the poll catches both a late load and
  // any change its callback or the `consent.onetrust` event miss.
  function check() {
    subscribeToOneTrust()
    const state = read()
    if (state) publish(state)
  }

  const waitForOneTrust = () =>
    new Promise<HostConsentState>(resolve => {
      const started = Date.now()
      const attempt = () => {
        subscribeToOneTrust()
        const state = read()
        if (state) return resolve(state)
        if (Date.now() - started >= loadTimeoutMs) return resolve({ status: 'unavailable' })
        win.setTimeout(attempt, Math.min(100, pollIntervalMs))
      }
      attempt()
    })

  const enable = (): Promise<HostConsentState> => {
    firstAnswer ??= waitForOneTrust().then(state => {
      lastSent = JSON.stringify(state)
      win.addEventListener('consent.onetrust', check)
      win.setInterval(check, pollIntervalMs)
      return state
    })
    return firstAnswer
  }

  return { enable }
}
